import React, { useEffect, useMemo, useState } from "react";
import NumberInput from "../../Components/Commons/Forms/NumberInput";
import { Row, Col } from "react-bootstrap";
import { Select } from "antd";
import BtnAdd from "../Commons/Buttons/BtnAdd";
import { toast } from "react-toastify";
import {
  createVital,
  editVital,
} from "../Helpers/API/VitalSignsApi/VitalSignsApi";
import DateTimeField from "../Commons/Forms/DateTimeField";
import { getMonths } from "../Helpers/Utilities/Common";

/**
 * --- VITAL SIGNS
 */
function VitalSignsForm({
  id,
  sex,
  patientDetails,
  addVital,
  setAddVital,
  showForm,
  setShowForm,
  formType,
  setFormType,
  fetchVitalSigns,
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [bmi, setBmi] = useState(0);
  const [ibw, setIbw] = useState(0);
  const [whr, setWhr] = useState(0);

  async function _createVitalSign() {
    setIsLoading(true);
    const response = await createVital({ ...addVital, bmi, ibw, whr }, id);
    if (response.status !== 200) {
      toast.error(response.response.data.messages.error_messages[0]);
    } else {
      toast.success(response.data.message);
      // reset form
      setAddVital({
        weight: "",
        weight_in_lb: "",
        height: "",
        height_in_ft: "",
        height_in_inch: "",
        bmi: "",
        ibw: "",
        waist: "",
        hip: "",
        whr: "",
        temperature: "",
        bp: "",
        heart_rate: "",
        respiratory_rate: "",
        oxygen_saturation: "",
        right_eye_wo_correction: "",
        left_eye_wo_correction: "",
        right_eye_w_correction: "",
        left_eye_w_correction: "",
        blood_type: "",
        last_meal: "",

        length: "",
        head_circumference: "",
        skinfold_thickness: "",
        body_circumference_waist: "",
        hip_in_cm: "",
        limbs: "",
        muac: "",
      });
      setShowForm(false);
      fetchVitalSigns();
    }
    setIsLoading(false);
  }

  async function _updateVitalSign() {
    setIsLoading(true);
    const response = await editVital({ ...addVital, bmi, ibw, whr }, id);
    if (response.status !== 200) {
      toast.error(response.response.data.messages.error_messages[0]);
    } else {
      toast.success(response.data.message);
      setAddVital({
        weight: "",
        weight_in_lb: "",
        height: "",
        height_in_ft: "",
        height_in_inch: "",
        bmi: "",
        ibw: "",
        waist: "",
        hip: "",
        whr: "",
        temperature: "",
        bp: "",
        heart_rate: "",
        respiratory_rate: "",
        oxygen_saturation: "",
        right_eye_wo_correction: "",
        left_eye_wo_correction: "",
        right_eye_w_correction: "",
        left_eye_w_correction: "",
        blood_type: "",
        last_meal: "",

        length: "",
        head_circumference: "",
        skinfold_thickness: "",
        body_circumference_waist: "",
        hip_in_cm: "",
        limbs: "",
        muac: "",
      });
      setFormType("add");
      setShowForm(false);
      fetchVitalSigns();
    }
    setIsLoading(false);
  }

  // const handleAddChange = (e) => {
  //   const { name, value } = e.target;
  //   setAddVital((prevState) => ({
  //     ...prevState,
  //     [name]: value,
  //   }));
  // };

  // const handleAddChange = (e) => {
  //   const { name, value } = e.target;
  //   if (name === "height_in_ft") {
  //     // If input is empty, reset both fields
  //     if (value.trim() === "") {
  //       setAddVital((prevState) => ({
  //         ...prevState,
  //         height_in_ft: "",
  //         height: "",
  //       }));
  //       return;
  //     }
  //     let cm = null;
  //     // Match format like 5'4" or 5'4
  //     const feetInchesMatch = value.match(/^(\d+)'(\d+)"?$/);
  //     // Match decimal feet like 5.5'
  //     const decimalFeetMatch = value.match(/^(\d+(\.\d+)?)'?$/);

  //     /* Supported Inputs:
  //       5'4" → 162.56 cm
  //       5'4 → 162.56 cm
  //       5.5' → 167.64 cm
  //       6 → 182.88 cm
  //     */

  //     if (feetInchesMatch) {
  //       const feet = parseInt(feetInchesMatch[1], 10);
  //       const inches = parseInt(feetInchesMatch[2], 10);
  //       const totalInches = feet * 12 + inches;
  //       cm = (totalInches * 2.54).toFixed(2);
  //     } else if (decimalFeetMatch) {
  //       const feet = parseFloat(decimalFeetMatch[1]);
  //       const totalInches = feet * 12;
  //       cm = (totalInches * 2.54).toFixed(2);
  //     }
  //     setAddVital((prevState) => ({
  //       ...prevState,
  //       height_in_ft: value,
  //       height: cm || prevState.height,
  //     }));
  //   } else {
  //     setAddVital((prevState) => ({
  //       ...prevState,
  //       [name]: value,
  //     }));
  //   }
  // };

  const handleAddChange = (e) => {
    const { name, value } = e.target;

    if (name === "height") {
      // Convert cm → inches → ft+in
      if (value.trim() === "") {
        setAddVital((prevState) => ({
          ...prevState,
          height_in_ft: "",
          height: "",
          height_in_inch: "",
        }));
        return;
      }

      const cm = parseFloat(value);
      if (!isNaN(cm)) {
        const totalInches = cm / 2.54;
        const feet = Math.floor(totalInches / 12);
        const inches = Math.round(totalInches % 12);

        const heightInFt = `${feet}'${inches}"`;

        setAddVital((prevState) => ({
          ...prevState,
          height: value, // keep cm
          height_in_inch: Math.round(totalInches), // inches
          height_in_ft: heightInFt,
        }));
      }
    } else if (name === "weight") {
      // Convert kg → lb
      if (value.trim() === "") {
        setAddVital((prevState) => ({
          ...prevState,
          weight: "",
          weight_in_lb: "",
        }));
        return;
      }

      const kg = parseFloat(value);
      if (!isNaN(kg)) {
        const lb = (kg * 2.20462).toFixed(2); // keep 2 decimals
        setAddVital((prevState) => ({
          ...prevState,
          weight: value, // kg
          weight_in_lb: lb,
        }));
      }
    } else if (name === "bp") {
      // Auto-format blood pressure as SYSTOLIC/DIASTOLIC while user types digits only
      const digitsOnly = value.replace(/\D/g, "");

      let formatted = "";

      if (digitsOnly.length <= 3) {
        // Still typing systolic value, no slash yet
        formatted = digitsOnly;
      } else if (digitsOnly.length === 4) {
        // Assume 2-digit systolic and 2-digit diastolic (e.g., 9080 -> 90/80)
        formatted = `${digitsOnly.slice(0, 2)}/${digitsOnly.slice(2)}`;
      } else {
        // 5+ digits: assume 3-digit systolic, remaining digits diastolic (e.g., 12080 -> 120/80)
        formatted = `${digitsOnly.slice(0, 3)}/${digitsOnly.slice(3)}`;
      }

      setAddVital((prevState) => ({
        ...prevState,
        bp: formatted,
      }));
    } else {
      // Other inputs
      if (name === "complaint") {
        setAddVital((prevState) => ({
          ...prevState,
          [name]: value?.toUpperCase(),
        }));
      } else {
        setAddVital((prevState) => ({
          ...prevState,
          [name]: value,
        }));
      }
    }
  };

  // Dynamically calculate BMI, WHR, and IBW
  useEffect(() => {
    var patientSex = sex ? sex.toLowerCase() : "";
    var inchOver5feet = 0;
    const { height, weight, waist, hip } = addVital;

    if (height) {
      inchOver5feet = parseFloat(height) / 0.0254 - 60;

      var ibw = 0;
      if (patientSex === "male") {
        ibw = 45.5 + 2.3 * inchOver5feet;
      } else {
        ibw = 50 + 2.3 * inchOver5feet;
      }
      setIbw(ibw.toFixed(2));
    } else {
      setIbw(0);
    }

    if (height && weight) {
      const heightInMeters = height / 100; // Convert height from cm to meters
      const calculatedBMI = weight / (heightInMeters * heightInMeters);
      setBmi(calculatedBMI.toFixed(2));
    } else {
      setBmi(0);
    }

    if (waist && hip) {
      const whr = parseFloat(waist / hip).toFixed(2);
      setWhr(whr);
    } else {
      setWhr(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addVital]);

  return (
    <div className="card-table">
      <div className="col pt-1 mb-4">
        <div className="row">
          <div className="col">
            <h5 className="font-bold">VITAL SIGNS HISTORY</h5>
          </div>
          <div className="col-3">
            <BtnAdd
              label={"+ Add Record"}
              size={"md"}
              onClick={(e) => {
                setFormType("add");
                setShowForm(!showForm);
                setAddVital({
                  weight: "",
                  weight_in_lb: "",
                  height: "",
                  height_in_ft: "",
                  height_in_inch: "",
                  bmi: "",
                  ibw: "",
                  waist: "",
                  hip: "",
                  whr: "",
                  temperature: "",
                  bp: "",
                  heart_rate: "",
                  respiratory_rate: "",
                  oxygen_saturation: "",
                  right_eye_wo_correction: "",
                  left_eye_wo_correction: "",
                  right_eye_w_correction: "",
                  left_eye_w_correction: "",
                  right_eye_vision: "",
                  left_eye_vision: "",
                  blood_type: "",
                  last_meal: "",

                  length: "",
                  head_circumference: "",
                  skinfold_thickness: "",
                  body_circumference_waist: "",
                  hip_in_cm: "",
                  limbs: "",
                  muac: "",
                });
              }}
            />
          </div>
        </div>
      </div>
      {showForm && (
        <div>
          {getMonths(patientDetails.birthdate) > 24 && (
            <>
              <Row>
                <Col xs={3} className="vital-signs-eachBar">
                  <DateTimeField
                    id="last_meal"
                    name="last_meal"
                    formLabel={`LAST MEAL`}
                    size={`sm`}
                    onChange={handleAddChange}
                    // isError={}
                    value={addVital.last_meal}
                    errorMessage="This is an error"
                  />
                </Col>
              </Row>
              <Row>
                <Col className="vital-signs-eachBar">
                  <NumberInput
                    id="weight"
                    name="weight"
                    formLabel={`WEIGHT (kg)`}
                    size={`sm`}
                    onChange={handleAddChange}
                    value={addVital.weight}
                    disabled={isLoading}
                  />
                </Col>
                <Col className="vital-signs-eachBar">
                  <NumberInput
                    id="weight_in_lb"
                    name="weight_in_lb"
                    formLabel={`WEIGHT (lb)`}
                    size={`sm`}
                    onChange={handleAddChange}
                    value={addVital.weight_in_lb}
                    disabled={isLoading}
                  />
                </Col>
                <Col className="vital-signs-eachBar">
                  <NumberInput
                    id="height"
                    name="height"
                    formLabel={`HEIGHT (cm)`}
                    size={`sm`}
                    onChange={handleAddChange}
                    value={addVital.height}
                    // disabled
                  />
                </Col>
                <Col className="vital-signs-eachBar">
                  <NumberInput
                    id="height_in_inch"
                    name="height_in_inch"
                    formLabel={`HEIGHT (in)`}
                    size={`sm`}
                    onChange={handleAddChange}
                    value={addVital.height_in_inch}
                    disabled
                  />
                </Col>
                {/* <Col className="vital-signs-eachBar">
                  <NumberInput
                    id="height_in_ft"
                    name="height_in_ft"
                    formLabel={`HEIGHT (ft)`}
                    size={`sm`}
                    onChange={handleAddChange}
                    value={addVital.height_in_ft}
                    disabled={isLoading}
                  />
                </Col> */}
              </Row>
              <Row>
                <Col className="vital-signs-eachBar">
                  <NumberInput
                    id="bmi"
                    name="bmi"
                    formLabel={`BMI`}
                    size={`sm`}
                    // value={addVital.height && addVital.weight ? addVital.bmi : ""}
                    value={bmi}
                    // onChange={handleAddChange}
                    disabled={true}
                  />
                </Col>
                <Col className="vital-signs-eachBar">
                  <NumberInput
                    id="ibw"
                    name="ibw"
                    formLabel={`IBW (kg)`}
                    size={`sm`}
                    // value={addVital.height ? addVital.ibw : "0.00"}
                    value={ibw}
                    // onChange={handleAddChange}
                    disabled={true}
                  />
                </Col>
                <Col className="vital-signs-eachBar">
                  <NumberInput
                    id="waist"
                    name="waist"
                    formLabel={`WAIST (in)`}
                    size={`sm`}
                    value={addVital.waist}
                    onChange={handleAddChange}
                    disabled={isLoading}
                  />
                </Col>
                <Col className="vital-signs-eachBar">
                  <NumberInput
                    id="hip"
                    name="hip"
                    formLabel={`HIP(in)`}
                    size={`sm`}
                    value={addVital.hip}
                    onChange={handleAddChange}
                    disabled={isLoading}
                  />
                </Col>
              </Row>
              <Row>
                <Col className="vital-signs-eachBar">
                  <NumberInput
                    id="whr"
                    name="whr"
                    formLabel={`WHR`}
                    size={`sm`}
                    // onChange={handleAddChange}
                    value={whr}
                    // value={
                    //   addVital.waist
                    //     ? parseFloat(addVital.waist / addVital.hip).toFixed(2)
                    //     : "0.00"
                    // }
                    // disabled={isLoading}
                    disabled
                  />
                </Col>
                <Col className="vital-signs-eachBar">
                  <NumberInput
                    id="temperature"
                    name="temperature"
                    formLabel={`TEMPERATURE (°C)`}
                    size={`sm`}
                    value={addVital.temperature}
                    onChange={handleAddChange}
                    disabled={isLoading}
                  />
                </Col>
                <Col className="vital-signs-eachBar">
                  <NumberInput
                    id="bp"
                    name="bp"
                    formLabel={`BLOOD PRESSURE (mmHg)`}
                    size={`sm`}
                    value={addVital.bp}
                    onChange={handleAddChange}
                    disabled={isLoading}
                  />
                </Col>
                <Col className="vital-signs-eachBar">
                  <NumberInput
                    id="heart_rate"
                    name="heart_rate"
                    formLabel={`HEART RATE (bpm)`}
                    size={`sm`}
                    value={addVital.heart_rate}
                    onChange={handleAddChange}
                    disabled={isLoading}
                  />
                </Col>
              </Row>
              <Row>
                <Col className="vital-signs-eachBar">
                  <NumberInput
                    id="respiratory_rate"
                    name="respiratory_rate"
                    formLabel={`RESPIRATORY RATE`}
                    size={`sm`}
                    value={addVital.respiratory_rate}
                    onChange={handleAddChange}
                    disabled={isLoading}
                  />
                </Col>
                <Col xs={3} className="vital-signs-eachBar">
                  <NumberInput
                    id="oxygen_saturation"
                    name="oxygen_saturation"
                    formLabel={`OXYGEN SATURATION`}
                    size={`sm`}
                    value={addVital.oxygen_saturation}
                    onChange={handleAddChange}
                    disabled={isLoading}
                  />
                </Col>
                <Col xs={3} className="vital-signs-eachBar">
                  <NumberInput
                    id="right_eye_wo_correction"
                    name="right_eye_wo_correction"
                    formLabel={`RIGHT EYE VISION (w/o correction)`}
                    size={`sm`}
                    value={addVital.right_eye_wo_correction}
                    onChange={handleAddChange}
                    disabled={isLoading}
                  />
                </Col>
                <Col xs={3} className="vital-signs-eachBar">
                  <NumberInput
                    id="left_eye_wo_correction"
                    name="left_eye_wo_correction"
                    formLabel={`LEFT EYE VISION (w/o correction)`}
                    size={`sm`}
                    value={addVital.left_eye_wo_correction}
                    onChange={handleAddChange}
                    disabled={isLoading}
                  />
                </Col>
                 <Col xs={3} className="vital-signs-eachBar">
                  <NumberInput
                    id="left_eye_w_correction"
                    name="left_eye_w_correction"
                    formLabel={`LEFT EYE VISION (w/ correction)`}
                    size={`sm`}
                    value={addVital.left_eye_w_correction}
                    onChange={handleAddChange}
                    disabled={isLoading}
                  />
                </Col>
                 <Col xs={3} className="vital-signs-eachBar">
                  <NumberInput
                    id="right_eye_w_correction"
                    name="right_eye_w_correction"
                    formLabel={`RIGHT EYE VISION (w/ correction)`}
                    size={`sm`}
                    value={addVital.right_eye_w_correction}
                    onChange={handleAddChange}
                    disabled={isLoading}
                  />
                </Col>
                {/* <div className="mb-3">
                            <NumberInput
                              id="right_eye_wo_correction"
                              name="right_eye_wo_correction"
                              formLabel={`RIGHT EYE VISION (w/o correction)`}
                              size={`sm`}
                              onChange={handleAddChange}
                            />
                          </div>
                          <div className="mb-3">
                            <NumberInput
                              id="left_eye_wo_correction"
                              name="left_eye_wo_correction"
                              formLabel={`LEFT EYE VISION (w/o correction)`}
                              size={`sm`}
                              onChange={handleAddChange}
                            />
                          </div>
                           <div className="mb-3">
                            <NumberInput
                              id="left_eye_w_correction"
                              name="left_eye_w_correction"
                              formLabel={`LEFT EYE VISION (w/ correction)`}
                              size={`sm`}
                              onChange={handleAddChange}
                            />
                          </div>
                           <div className="mb-3">
                            <NumberInput
                              id="right_eye_w_correction"
                              name="right_eye_w_correction"
                              formLabel={`RIGHT EYE VISION (w/ correction)`}
                              size={`sm`}
                              onChange={handleAddChange}
                            />
                          </div> */}
              </Row>
                <Col xs={3} className="vital-signs-eachBar">
                  <div className="form-label">BLOOD TYPE</div>
                  <Select
                    placeholder="SELECT"
                    className="custom-select-height w-full"
                    value={addVital.blood_type}
                    options={[
                      { value: "A+", label: "A+" },
                      { value: "B+", label: "B+" },
                      { value: "AB+", label: "AB+" },
                      { value: "O+", label: "O+" },
                      { value: "A-", label: "A-" },
                      { value: "B-", label: "B-" },
                      { value: "AB-", label: "AB-" },
                      { value: "O-", label: "O-" },
                    ]}
                    size="small"
                    onChange={(value) =>
                      handleAddChange({
                        target: { name: "blood_type", value },
                      })
                    }
                    showSearch
                    disabled={isLoading}
                  />
                </Col>
            </>
          )}
          {getMonths(patientDetails.birthdate) <= 24 && (
            <>
              <Row>
                <Col className="vital-signs-eachBar">
                  <DateTimeField
                    id="last_meal"
                    name="last_meal"
                    formLabel={`LAST MEAL`}
                    size={`sm`}
                    onChange={handleAddChange}
                    value={addVital.last_meal}
                    errorMessage="This is an error"
                  />
                </Col>
                <Col className="vital-signs-eachBar">
                  <NumberInput
                    id="weight"
                    name="weight"
                    formLabel={`WEIGHT (kg)`}
                    size={`sm`}
                    onChange={handleAddChange}
                    value={addVital.weight}
                    disabled={isLoading}
                  />
                </Col>
                <Col className="vital-signs-eachBar">
                  <NumberInput
                    id="weight_in_lb"
                    name="weight_in_lb"
                    formLabel={`WEIGHT (lb)`}
                    value={addVital.weight_in_lb}
                    size={`sm`}
                    onChange={handleAddChange}
                    // isError={}
                    errorMessage="This is an error"
                    disabled={true}
                  />
                </Col>
                <Col className="vital-signs-eachBar">
                  <NumberInput
                    id="length"
                    name="length"
                    formLabel={`LENGTH (cm)`}
                    size={`sm`}
                    onChange={handleAddChange}
                    // isError={}
                    errorMessage="This is an error"
                  />
                </Col>
              </Row>
              <Row>
                <Col className="vital-signs-eachBar">
                  <NumberInput
                    id="head_circumference"
                    name="head_circumference"
                    formLabel={`HEAD CIRCUMFERENCE (cm)`}
                    size={`sm`}
                    onChange={handleAddChange}
                    // isError={}
                    errorMessage="This is an error"
                  />
                </Col>
                <Col className="vital-signs-eachBar">
                  <NumberInput
                    id="skinfold_thickness"
                    name="skinfold_thickness"
                    formLabel={`SKINFOLD THICKNESS (cm)`}
                    size={`sm`}
                    onChange={handleAddChange}
                    // isError={}
                    errorMessage="This is an error"
                  />
                </Col>
                <Col className="vital-signs-eachBar">
                  <NumberInput
                    id="body_circumference_waist"
                    name="body_circumference_waist"
                    formLabel={`BODY CIRCUMFERENCE WAIST (cm)`}
                    size={`sm`}
                    onChange={handleAddChange}
                    // isError={}
                    errorMessage="This is an error"
                  />
                </Col>
                <Col className="vital-signs-eachBar">
                  <NumberInput
                    id="hip_in_cm"
                    name="hip_in_cm"
                    formLabel={`HIP (cm)`}
                    size={`sm`}
                    onChange={handleAddChange}
                    // isError={}
                    errorMessage="This is an error"
                  />
                </Col>
              </Row>
              <Row>
                <Col className="vital-signs-eachBar">
                  <NumberInput
                    id="limbs"
                    name="limbs"
                    formLabel={`LIMBS (cm)`}
                    size={`sm`}
                    onChange={handleAddChange}
                    // isError={}
                    errorMessage="This is an error"
                  />
                </Col>
                <Col className="vital-signs-eachBar">
                  <NumberInput
                    id="muac"
                    name="muac"
                    formLabel={`MIDDLE UPPER ARM CIRCUMFERENCE (cm)`}
                    size={`sm`}
                    onChange={handleAddChange}
                    // isError={}
                    errorMessage="This is an error"
                  />
                </Col>
                <Col className="vital-signs-eachBar">
                  <NumberInput
                    id="temperature"
                    name="temperature"
                    formLabel={`TEMPERATURE (°C)`}
                    size={`sm`}
                    onChange={handleAddChange}
                    // isError={}
                    errorMessage="This is an error"
                  />
                </Col>
              </Row>
                            <Row>
                <Col xs={3} className="vital-signs-eachBar">
                  <div className="form-label">BLOOD TYPE</div>
                  <Select
                    placeholder="SELECT"
                    className="custom-select-height w-full"
                    value={addVital.blood_type}
                    options={[
                      { value: "A+", label: "A+" },
                      { value: "B+", label: "B+" },
                      { value: "AB+", label: "AB+" },
                      { value: "O+", label: "O+" },
                      { value: "A-", label: "A-" },
                      { value: "B-", label: "B-" },
                      { value: "AB-", label: "AB-" },
                      { value: "O-", label: "O-" },
                    ]}
                    size="small"
                    onChange={(value) =>
                      handleAddChange({
                        target: { name: "blood_type", value },
                      })
                    }
                    showSearch
                    disabled={isLoading}
                  />
                </Col>
              </Row>
            </>
          )}
          <div className="row justify-content-end mt-5 mb-3">
            <div className="col-sm-3">
              <BtnAdd
                id={"green-btn"}
                label={formType === "edit" ? "Save changes" : "Submit"}
                size={"sm"}
                onClick={
                  formType === "edit" ? _updateVitalSign : _createVitalSign
                }
                disabled={Object.values(addVital).every(
                  (value) => value === ""
                )}
                isClicked={isLoading}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default VitalSignsForm;
